import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { normalizeDraftMatterStatus } from "@/lib/draft-matter-status";
import type { DraftMatter } from "@/types/drafting";

export interface DraftMatterFilters {
  search: string;
  contractType: string;
  jurisdiction: string;
  status: string;
  fromDate: string;
  toDate: string;
}

export const EMPTY_DRAFT_MATTER_FILTERS: DraftMatterFilters = {
  search: "",
  contractType: "",
  jurisdiction: "",
  status: "",
  fromDate: "",
  toDate: "",
};

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "setup", label: "Setup" },
  { value: "background", label: "Background" },
  { value: "drafting", label: "Draft" },
  { value: "review", label: "Review" },
  { value: "export", label: "Export" },
  { value: "completed", label: "Completed" },
];

export function hasActiveDraftMatterFilters(filters: DraftMatterFilters): boolean {
  return Boolean(
    filters.search.trim() ||
      filters.contractType ||
      filters.jurisdiction ||
      filters.status ||
      filters.fromDate ||
      filters.toDate
  );
}

export function applyDraftMatterFilters(
  list: DraftMatter[],
  filters: DraftMatterFilters
): DraftMatter[] {
  let result = list;
  const q = filters.search.trim().toLowerCase();
  if (q) {
    result = result.filter((matter) => {
      const title = (matter.title ?? "").toLowerCase();
      const number = matter.matter_number.toLowerCase();
      const type = (matter.contract_type ?? "").toLowerCase();
      return title.includes(q) || number.includes(q) || type.includes(q);
    });
  }
  if (filters.contractType) {
    result = result.filter((matter) => matter.contract_type === filters.contractType);
  }
  if (filters.jurisdiction) {
    result = result.filter((matter) => matter.jurisdiction === filters.jurisdiction);
  }
  if (filters.status) {
    result = result.filter(
      (matter) => normalizeDraftMatterStatus(matter.status) === filters.status
    );
  }
  if (filters.fromDate) {
    const from = new Date(filters.fromDate);
    from.setHours(0, 0, 0, 0);
    result = result.filter((matter) => new Date(matter.created_at) >= from);
  }
  if (filters.toDate) {
    const to = new Date(filters.toDate);
    to.setHours(23, 59, 59, 999);
    result = result.filter((matter) => new Date(matter.created_at) <= to);
  }
  return result;
}

interface DraftMatterFilterBarProps {
  filters: DraftMatterFilters;
  onChange: (next: DraftMatterFilters) => void;
  contractTypeOptions: string[];
  jurisdictionOptions: string[];
  mobileSheetOpen: boolean;
  onMobileSheetOpenChange: (open: boolean) => void;
}

export function DraftMatterFilterBar({
  filters,
  onChange,
  contractTypeOptions,
  jurisdictionOptions,
  mobileSheetOpen,
  onMobileSheetOpenChange,
}: DraftMatterFilterBarProps) {
  const set = (patch: Partial<DraftMatterFilters>) => onChange({ ...filters, ...patch });

  const dateInputClassName =
    "block box-border h-10 w-full min-w-0 max-w-full border-[rgba(15,23,42,0.09)] bg-white text-[#101828] shadow-[var(--shadow-card)] [color-scheme:light] dark:border-indigo-300/20 dark:bg-slate-950/45 dark:text-slate-100 dark:shadow-none dark:[color-scheme:dark]";
  const searchInputClassName =
    "border-[var(--lm-border-subtle)] bg-white text-sm text-[#101828] shadow-[var(--shadow-card)] placeholder:text-[var(--lm-muted)] dark:border-indigo-300/20 dark:bg-slate-950/45 dark:text-slate-100 dark:shadow-none dark:placeholder:text-slate-500";
  const fieldLabelClassName =
    "mb-1 block text-[11px] font-medium text-[var(--lm-muted)] dark:text-slate-400";

  const renderSearchField = () => (
    <div className="min-w-0 sm:min-w-[140px] sm:flex-1">
      <label className={fieldLabelClassName}>Search</label>
      <Input
        placeholder="Title or matter #…"
        value={filters.search}
        onChange={(e) => set({ search: e.target.value })}
        className={searchInputClassName}
      />
    </div>
  );

  const renderContractTypeField = () => (
    <div className="min-w-0 sm:w-[150px] sm:shrink-0">
      <label className={fieldLabelClassName}>Template</label>
      <Select
        value={filters.contractType || "all"}
        onValueChange={(v) => set({ contractType: v === "all" ? "" : v })}
      >
        <SelectTrigger className="w-full [&>span]:line-clamp-1 [&>span]:truncate">
          <SelectValue placeholder="All templates" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All templates</SelectItem>
          {contractTypeOptions.map((t) => (
            <SelectItem key={t} value={t}>
              {t}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const renderJurisdictionField = () => (
    <div className="min-w-0 sm:w-[150px] sm:shrink-0">
      <label className={fieldLabelClassName}>Jurisdiction</label>
      <Select
        value={filters.jurisdiction || "all"}
        onValueChange={(v) => set({ jurisdiction: v === "all" ? "" : v })}
      >
        <SelectTrigger className="w-full [&>span]:line-clamp-1 [&>span]:truncate">
          <SelectValue placeholder="All jurisdictions" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All jurisdictions</SelectItem>
          {jurisdictionOptions.map((j) => (
            <SelectItem key={j} value={j}>
              {j}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const renderStatusField = () => (
    <div className="min-w-0 sm:w-[130px] sm:shrink-0">
      <label className={fieldLabelClassName}>Status</label>
      <Select
        value={filters.status || "all"}
        onValueChange={(v) => set({ status: v === "all" ? "" : v })}
      >
        <SelectTrigger className="w-full [&>span]:line-clamp-1 [&>span]:truncate">
          <SelectValue placeholder="All statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          {STATUS_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const renderFromDateField = () => (
    <div className="min-w-0 sm:w-[130px] sm:shrink-0">
      <label className={fieldLabelClassName}>From date</label>
      <Input
        type="date"
        value={filters.fromDate}
        onChange={(e) => set({ fromDate: e.target.value })}
        className={dateInputClassName}
      />
    </div>
  );

  const renderToDateField = () => (
    <div className="min-w-0 sm:w-[130px] sm:shrink-0">
      <label className={fieldLabelClassName}>To date</label>
      <Input
        type="date"
        value={filters.toDate}
        onChange={(e) => set({ toDate: e.target.value })}
        className={dateInputClassName}
      />
    </div>
  );

  const renderClearButton = () =>
    hasActiveDraftMatterFilters(filters) ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mb-0.5 shrink-0 whitespace-nowrap"
        onClick={() => onChange(EMPTY_DRAFT_MATTER_FILTERS)}
      >
        Clear
      </Button>
    ) : null;

  return (
    <>
      <Sheet open={mobileSheetOpen} onOpenChange={onMobileSheetOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <SheetBody className="space-y-3">
            {renderSearchField()}
            {renderContractTypeField()}
            {renderJurisdictionField()}
            {renderStatusField()}
            {renderFromDateField()}
            {renderToDateField()}
            {renderClearButton()}
          </SheetBody>
        </SheetContent>
      </Sheet>

      <div className={cn("mb-4 hidden w-full flex-wrap items-end gap-2.5 sm:flex")}>
        {renderSearchField()}
        {renderContractTypeField()}
        {renderJurisdictionField()}
        {renderStatusField()}
        {renderFromDateField()}
        {renderToDateField()}
        {renderClearButton()}
      </div>
    </>
  );
}
